import { supabase } from './supabase';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';

interface RequestConfig extends RequestInit {
  skipAuth?: boolean;
  retryCount?: number;
}

/**
 * Get current session from Supabase.
 * 
 * IMPORTANT: We do NOT manually refresh here anymore!
 * The Supabase SDK with autoRefreshToken: true handles refresh automatically.
 * Manual refresh causes "refresh_token_already_used" errors due to race conditions.
 */
const getSession = async (): Promise<string> => {
  try {
    const { data: { session }, error } = await supabase.auth.getSession();

    if (error) {
      console.error('[apiClient] Failed to get session:', error);
      throw new Error(`Failed to get session: ${error.message}`);
    }

    if (!session?.access_token) {
      throw new Error('No active session');
    }

    return session.access_token;
  } catch (error) {
    console.error('[apiClient] getSession error:', error);
    throw error;
  }
};

/**
 * Get authentication headers with current session token
 */
const getAuthHeaders = async (): Promise<HeadersInit> => {
  const token = await getSession();

  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`,
  };
};

/**
 * Enhanced fetch with proactive token refresh
 * Guarantees that expired tokens NEVER reach the backend
 */
export const fetchWithAuth = async (
  url: string,
  config: RequestConfig = {}
): Promise<Response> => {
  const { skipAuth = false, retryCount = 0, ...fetchConfig } = config;

  // For requests that don't need auth (like login/register)
  if (skipAuth) {
    return fetch(url, {
      ...fetchConfig,
      headers: {
        'Content-Type': 'application/json',
        ...fetchConfig.headers,
      },
    });
  }

  // Proactively get fresh token (refreshes if needed)
  let headers: HeadersInit;
  try {
    headers = await getAuthHeaders();
  } catch (error) {
    console.error('[apiClient] Failed to get auth headers:', error);

    // If we can't get a valid token, logout and redirect
    // BUT: Don't redirect if we're on auth/waitlist pages (user was just logged out intentionally)
    const currentPath = window.location.pathname;
    const isAuthRelatedPage = currentPath === '/waitlist-confirmation' || currentPath === '/auth/callback';
    if (!isAuthRelatedPage) {
      await supabase.auth.signOut();
      window.location.href = '/login';
    }
    throw new Error('Authentication failed. Please login again.');
  }

  // Make the request with guaranteed fresh token
  const response = await fetch(url, {
    ...fetchConfig,
    headers: {
      ...headers,
      ...fetchConfig.headers,
    },
  });

  // If not 401, return response as-is
  if (response.status !== 401) {
    return response;
  }

  // Got 401 - token might be expired or invalid
  // Don't manually refresh - let Supabase SDK handle it via autoRefreshToken
  // Retry with increasing delays to give SDK time to refresh
  console.warn(`[apiClient] Got 401, retry ${retryCount + 1}/3 after delay...`);

  // Allow up to 3 retries before giving up (more resilient to temporary issues)
  const MAX_RETRIES = 3;
  if (retryCount >= MAX_RETRIES) {
    console.error('[apiClient] 401 persisted after', MAX_RETRIES, 'retries for:', url);
    // Session is truly invalid, redirect to login
    const currentPath = window.location.pathname;
    const isAuthRelatedPage = currentPath === '/waitlist-confirmation' || currentPath === '/auth/callback';
    if (!isAuthRelatedPage) {
      await supabase.auth.signOut({ scope: 'local' });
      window.location.href = '/login';
    }
    throw new Error('Session expired. Please login again.');
  }

  // Exponential backoff: 1s, 2s, 4s to give Supabase SDK time to refresh
  const delay = Math.min(1000 * Math.pow(2, retryCount), 4000);
  await new Promise(r => setTimeout(r, delay));

  // Retry the request - getSession will return the refreshed token if SDK refreshed it
  return fetchWithAuth(url, { ...config, retryCount: retryCount + 1 });
};

/**
 * Convenience wrapper for GET requests
 */
export const apiGet = async <T = any>(
  endpoint: string,
  config?: RequestConfig
): Promise<T> => {
  const url = endpoint.startsWith('http') ? endpoint : `${API_URL}${endpoint}`;
  const response = await fetchWithAuth(url, {
    ...config,
    method: 'GET',
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || `Request failed with status ${response.status}`);
  }

  return response.json();
};

/**
 * Convenience wrapper for POST requests
 */
export const apiPost = async <T = any>(
  endpoint: string,
  data?: any,
  config?: RequestConfig
): Promise<T> => {
  const url = endpoint.startsWith('http') ? endpoint : `${API_URL}${endpoint}`;
  const response = await fetchWithAuth(url, {
    ...config,
    method: 'POST',
    body: data ? JSON.stringify(data) : undefined,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || `Request failed with status ${response.status}`);
  }

  return response.json();
};

/**
 * Convenience wrapper for PUT requests
 */
export const apiPut = async <T = any>(
  endpoint: string,
  data?: any,
  config?: RequestConfig
): Promise<T> => {
  const url = endpoint.startsWith('http') ? endpoint : `${API_URL}${endpoint}`;
  const response = await fetchWithAuth(url, {
    ...config,
    method: 'PUT',
    body: data ? JSON.stringify(data) : undefined,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || `Request failed with status ${response.status}`);
  }

  return response.json();
};

/**
 * Convenience wrapper for PATCH requests
 */
export const apiPatch = async <T = any>(
  endpoint: string,
  data?: any,
  config?: RequestConfig
): Promise<T> => {
  const url = endpoint.startsWith('http') ? endpoint : `${API_URL}${endpoint}`;
  const response = await fetchWithAuth(url, {
    ...config,
    method: 'PATCH',
    body: data ? JSON.stringify(data) : undefined,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || `Request failed with status ${response.status}`);
  }

  return response.json();
};

/**
 * Convenience wrapper for DELETE requests
 */
export const apiDelete = async <T = any>(
  endpoint: string,
  config?: RequestConfig
): Promise<T> => {
  const url = endpoint.startsWith('http') ? endpoint : `${API_URL}${endpoint}`;
  const response = await fetchWithAuth(url, {
    ...config,
    method: 'DELETE',
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || `Request failed with status ${response.status}`);
  }

  // Handle 204 No Content responses
  if (response.status === 204) {
    return undefined as T;
  }

  return response.json();
};

export default {
  get: apiGet,
  post: apiPost,
  put: apiPut,
  patch: apiPatch,
  delete: apiDelete,
  fetchWithAuth,
};
